import React, { useState } from 'react';
import { 
  Card, 
  Collapse, 
  Typography, 
  Row, 
  Col, 
  Statistic, 
  Tooltip, 
  Tag, 
  Alert, 
  Progress,
  Space,
  Divider,
  Button
} from 'antd';
import {
  InfoCircleOutlined,
  QuestionCircleOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  CalculatorOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const CostExplanationPanel = ({ costData }) => {
  const [expandedPanels, setExpandedPanels] = useState(['overview', 'formulas']);

  if (!costData || !costData.serviceCosts) {
    return (
      <Alert
        message="Cost data not available"
        description="Cost tracking data is being collected. Please try again in a few minutes."
        type="info"
        showIcon
      />
    );
  }

  // Cost calculation explanations
  const costFormulas = {
    frameExtractor: {
      name: "Frame Extractor",
      description: "Extracts video frames using FFmpeg with optimized CPU/memory allocation",
      formula: "CPU_TIME × CPU_RATE + MEMORY_GB × MEMORY_RATE + BASE_REQUEST_COST",
      factors: [
        { name: "CPU Time", description: "Processing time × CPU cores allocated", weight: "~70%" },
        { name: "Memory Usage", description: "Peak memory consumption during processing", weight: "~20%" },
        { name: "Base Cost", description: "Fixed cost per request/invocation", weight: "~10%" }
      ],
      optimizations: [
        "Use parallel processing for multiple video segments",
        "Optimize FFmpeg parameters for faster encoding",
        "Right-size CPU allocation based on video complexity"
      ]
    },
    soloAnalyzer: {
      name: "Solo Analyzer", 
      description: "AI-powered frame analysis using computer vision models",
      formula: "AI_PROCESSING_TIME × GPU_RATE + MODEL_MEMORY × MEMORY_RATE + INFERENCE_COST",
      factors: [
        { name: "AI Processing", description: "Model inference time per frame", weight: "~80%" },
        { name: "GPU/CPU Usage", description: "Compute resources for AI model execution", weight: "~15%" },
        { name: "Model Loading", description: "Memory cost for loading AI models", weight: "~5%" }
      ],
      optimizations: [
        "Batch multiple frames per inference call",
        "Use GPU acceleration when available", 
        "Optimize model size vs accuracy trade-offs"
      ]
    },
    facemeshAnalyzer: {
      name: "Facemesh Analyzer",
      description: "Facial landmark detection and mesh analysis using MediaPipe",
      formula: "FACE_DETECTION_TIME × CPU_RATE + LANDMARK_PROCESSING × MEMORY_RATE",
      factors: [
        { name: "Face Detection", description: "Time to detect faces in frame", weight: "~60%" },
        { name: "Landmark Analysis", description: "Extract facial landmarks and mesh", weight: "~30%" },
        { name: "Memory Usage", description: "Memory for face detection models", weight: "~10%" }
      ],
      optimizations: [
        "Skip frames without detected faces",
        "Use lower resolution for face detection",
        "Optimize MediaPipe model parameters"
      ]
    },
    batchedAggregator: {
      name: "Batched Aggregator", 
      description: "Collects and aggregates analysis results with progress tracking",
      formula: "DATABASE_WRITES × WRITE_COST + PROCESSING_TIME × CPU_RATE + STORAGE_COST",
      factors: [
        { name: "Database Operations", description: "Firestore read/write operations", weight: "~50%" },
        { name: "Data Processing", description: "Result aggregation and computation", weight: "~30%" },
        { name: "Storage Cost", description: "Temporary storage for batched results", weight: "~20%" }
      ],
      optimizations: [
        "Batch database writes efficiently",
        "Optimize aggregation algorithms", 
        "Use appropriate database indexing"
      ]
    }
  };

  const renderServiceCostBreakdown = (serviceName, serviceData) => {
    const config = costFormulas[serviceName.replace('-', '')] || costFormulas.soloAnalyzer;
    const breakdown = serviceData.breakdown || {};
    
    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Statistic
              title="Total Cost"
              value={serviceData.totalCost}
              precision={6}
              prefix={<DollarOutlined />}
              valueStyle={{ fontSize: 18, fontWeight: 'bold' }}
            />
          </Col>
          <Col xs={24} md={8}>
            <Statistic
              title="Frames Processed"
              value={serviceData.frameCount}
              suffix="frames"
              prefix={<DatabaseOutlined />}
            />
          </Col>
          <Col xs={24} md={8}>
            <Statistic
              title="Avg Processing Time"
              value={serviceData.avgProcessingTime}
              precision={0}
              suffix="ms"
              prefix={<ClockCircleOutlined />}
            />
          </Col>
        </Row>
        
        <Divider />
        
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Title level={5}>
              <CalculatorOutlined /> Cost Formula
            </Title>
            <Paragraph>
              <Text code style={{ fontSize: 12 }}>
                {config.formula}
              </Text>
            </Paragraph>
            <Paragraph type="secondary">
              {config.description}
            </Paragraph>
          </Col>
          
          <Col xs={24} md={12}>
            <Title level={5}>Cost Breakdown</Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Processing Cost</Text>
                <Progress 
                  percent={70} 
                  size="small" 
                  strokeColor="#1890ff"
                  format={() => `$${breakdown.processingCost?.toFixed(6) || '0.000000'}`}
                />
              </div>
              <div>
                <Text strong>Memory Cost</Text>
                <Progress 
                  percent={20} 
                  size="small" 
                  strokeColor="#52c41a"
                  format={() => `$${breakdown.memoryCost?.toFixed(6) || '0.000000'}`}
                />
              </div>
              <div>
                <Text strong>Base Cost</Text>
                <Progress 
                  percent={10} 
                  size="small" 
                  strokeColor="#faad14"
                  format={() => `$${breakdown.baseCost?.toFixed(6) || '0.000000'}`}
                />
              </div>
            </Space>
          </Col>
        </Row>

        <Divider />
        
        <Title level={5}>Cost Factors</Title>
        <Row gutter={[8, 8]}>
          {config.factors.map((factor, index) => (
            <Col xs={24} sm={8} key={index}>
              <Card size="small" bodyStyle={{ padding: 12 }}>
                <div style={{ textAlign: 'center' }}>
                  <Tag color="blue">{factor.weight}</Tag>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{factor.name}</Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {factor.description}
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <Divider />
        
        <Title level={5}>💡 Optimization Tips</Title>
        <ul>
          {config.optimizations.map((tip, index) => (
            <li key={index}>
              <Text>{tip}</Text>
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  const renderOverallCostMetrics = () => (
    <Card>
      <Title level={4}>
        <CalculatorOutlined /> Cost Calculation Methodology
      </Title>
      
      <Row gutter={[16, 24]}>
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic
              title="Cost Per Frame"
              value={costData.costPerFrame}
              precision={6}
              prefix={<DollarOutlined />}
              suffix="/ frame"
            />
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
              Total cost divided by frames processed across all services
            </Paragraph>
          </Card>
        </Col>
        
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic
              title="Cost Per Video"
              value={costData.costPerVideo}
              precision={4}
              prefix={<DollarOutlined />}
              suffix="/ video"
            />
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
              Average cost based on completed videos in time window
            </Paragraph>
          </Card>
        </Col>
        
        <Col xs={24} md={8}>
          <Card size="small">
            <Statistic
              title="Cost Per Hour"
              value={costData.costPerHour}
              precision={4}
              prefix={<DollarOutlined />}
              suffix="/ hour"
            />
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
              Hourly processing cost rate based on current usage
            </Paragraph>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Alert
        message="Cost Calculation Principles"
        description={
          <div>
            <Paragraph>
              <strong>Cloud Run Pricing Model:</strong> Costs are calculated based on actual CPU time, memory usage, 
              and request count. Each service is billed separately for the resources it consumes.
            </Paragraph>
            <ul>
              <li><strong>CPU Cost:</strong> $0.00001667 per vCPU-second (approximately)</li>
              <li><strong>Memory Cost:</strong> $0.00000167 per GiB-second (approximately)</li>
              <li><strong>Request Cost:</strong> $0.0000004 per request (first 2M requests free)</li>
            </ul>
            <Paragraph>
              <Text type="secondary">
                * Exact rates may vary by region and are subject to Google Cloud pricing updates.
                This dashboard shows actual measured costs from your pipeline execution.
              </Text>
            </Paragraph>
          </div>
        }
        type="info"
        showIcon
      />
    </Card>
  );

  return (
    <div style={{ marginTop: 24 }}>
      <Collapse 
        activeKey={expandedPanels}
        onChange={setExpandedPanels}
        size="large"
      >
        <Panel 
          header={
            <Space>
              <CalculatorOutlined />
              <span>📊 Detailed Cost Formulas</span>
              <Tag color="red">Complete Methodology</Tag>
            </Space>
          } 
          key="formulas"
        >
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>Complete Cost Calculation Methodology</Title>
            
            {/* Frame Extractor Costs */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Title level={5}>🎬 Frame Extractor Costs</Title>
              <div style={{ background: '#f6ffed', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                <Text code style={{ fontSize: '13px' }}>
                  <strong>Total Cost = Processing Cost + Memory Cost + Base Cost</strong>
                </Text>
              </div>
              <Row gutter={[16, 8]}>
                <Col span={8}>
                  <Text strong>Processing Cost:</Text><br/>
                  <Text code>CPU Time × $0.0000241/vCPU-second</Text>
                </Col>
                <Col span={8}>
                  <Text strong>Memory Cost:</Text><br/>
                  <Text code>Memory × Duration × $0.00000256/GB-second</Text>
                </Col>
                <Col span={8}>
                  <Text strong>Base Cost:</Text><br/>
                  <Text code>$0.0000004 per request</Text>
                </Col>
              </Row>
              <Divider style={{ margin: '12px 0' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <strong>Example:</strong> Processing 800MB video (120ms processing, 4GB memory, 16 CPU cores):<br/>
                Processing: 0.12s × 16 vCPUs × $0.0000241 = $0.046<br/>
                Memory: 4GB × 0.12s × $0.00000256 = $0.001<br/>
                Base: $0.0000004<br/>
                <strong>Total: $0.047 per video</strong>
              </Text>
            </Card>

            {/* Solo Analyzer Costs */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Title level={5}>🤖 Solo-Analyzer (AI Processing) Costs</Title>
              <div style={{ background: '#f6ffed', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                <Text code style={{ fontSize: '13px' }}>
                  <strong>Total Cost = Processing Cost + Memory Cost + Base Cost</strong>
                </Text>
              </div>
              <Row gutter={[16, 8]}>
                <Col span={8}>
                  <Text strong>Processing Cost:</Text><br/>
                  <Text code>AI Analysis Time × $0.0000241/vCPU-second</Text>
                </Col>
                <Col span={8}>
                  <Text strong>Memory Cost:</Text><br/>
                  <Text code>Model Memory × Duration × $0.00000256/GB-second</Text>
                </Col>
                <Col span={8}>
                  <Text strong>Base Cost:</Text><br/>
                  <Text code>$0.0000004 per frame</Text>
                </Col>
              </Row>
              <Divider style={{ margin: '12px 0' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <strong>Example:</strong> Analyzing 1 frame (300ms AI processing, 2GB model memory, 4 CPU cores):<br/>
                Processing: 0.3s × 4 vCPUs × $0.0000241 = $0.029<br/>
                Memory: 2GB × 0.3s × $0.00000256 = $0.002<br/>
                Base: $0.0000004<br/>
                <strong>Total: $0.031 per frame</strong>
              </Text>
            </Card>

            {/* Cost Factors */}
            <Card size="small" style={{ background: '#fafafa' }}>
              <Title level={5}>⚙️ Cost Variables & Factors</Title>
              <Row gutter={[16, 12]}>
                <Col span={12}>
                  <Text strong>Processing Time Factors:</Text>
                  <ul style={{ margin: '8px 0', paddingLeft: 16 }}>
                    <li>Video resolution (higher = slower)</li>
                    <li>AI model complexity</li>
                    <li>Number of faces detected</li>
                    <li>CPU allocation per service</li>
                  </ul>
                </Col>
                <Col span={12}>
                  <Text strong>Memory Factors:</Text>
                  <ul style={{ margin: '8px 0', paddingLeft: 16 }}>
                    <li>AI model size (2-4GB typical)</li>
                    <li>Frame buffer size</li>
                    <li>Concurrent processing</li>
                    <li>Memory allocation per service</li>
                  </ul>
                </Col>
              </Row>
              <Divider style={{ margin: '12px 0' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <InfoCircleOutlined /> <strong>Note:</strong> Costs are based on Google Cloud Run pricing. 
                Actual costs may vary based on regional pricing, sustained usage discounts, and resource optimization.
              </Text>
            </Card>
          </div>
        </Panel>

        <Panel 
          header={
            <Space>
              <InfoCircleOutlined />
              <span>Cost Calculation Overview</span>
              <Tag color="blue">Essential</Tag>
            </Space>
          } 
          key="overview"
        >
          {renderOverallCostMetrics()}
        </Panel>

        {Object.entries(costData.serviceCosts).map(([serviceName, serviceData]) => (
          <Panel 
            header={
              <Space>
                <CloudServerOutlined />
                <span>{costFormulas[serviceName.replace('-', '')]?.name || serviceName}</span>
                <Tag color="green">${serviceData.totalCost.toFixed(6)}</Tag>
                <Tag color="orange">{serviceData.frameCount} frames</Tag>
              </Space>
            } 
            key={serviceName}
          >
            {renderServiceCostBreakdown(serviceName, serviceData)}
          </Panel>
        ))}

        <Panel 
          header={
            <Space>
              <QuestionCircleOutlined />
              <span>Frequently Asked Questions</span>
              <Tag color="purple">Help</Tag>
            </Space>
          } 
          key="faq"
        >
          <Card>
            <Collapse ghost>
              <Panel header="Why do costs vary between similar videos?" key="1">
                <Paragraph>
                  Cost variation depends on several factors:
                  <ul>
                    <li><strong>Video complexity:</strong> More complex scenes require longer processing times</li>
                    <li><strong>Frame count:</strong> Longer videos have more frames to process</li>
                    <li><strong>Face detection:</strong> Videos with more faces require additional analysis</li>
                    <li><strong>Resolution:</strong> Higher resolution videos consume more memory and CPU</li>
                    <li><strong>Cold starts:</strong> First requests may include model loading time</li>
                  </ul>
                </Paragraph>
              </Panel>
              
              <Panel header="How can I reduce processing costs?" key="2">
                <Paragraph>
                  Cost optimization strategies:
                  <ul>
                    <li><strong>Batch processing:</strong> Process multiple videos together to reduce cold starts</li>
                    <li><strong>Right-sizing:</strong> Adjust CPU/memory allocation based on video complexity</li>
                    <li><strong>Frame sampling:</strong> Process every Nth frame for less critical analysis</li>
                    <li><strong>Region selection:</strong> Use the most cost-effective cloud region</li>
                    <li><strong>Time-based scheduling:</strong> Process during off-peak hours if possible</li>
                  </ul>
                </Paragraph>
              </Panel>
              
              <Panel header="What happens if processing fails?" key="3">
                <Paragraph>
                  Failed processing handling:
                  <ul>
                    <li><strong>Partial costs:</strong> You only pay for successfully processed frames</li>
                    <li><strong>Retry mechanism:</strong> Failed jobs are automatically retried up to 3 times</li>
                    <li><strong>Error tracking:</strong> Detailed error logs help identify issues</li>
                    <li><strong>Monitoring:</strong> Real-time alerts notify you of processing failures</li>
                  </ul>
                </Paragraph>
              </Panel>
            </Collapse>
          </Card>
        </Panel>
      </Collapse>
    </div>
  );
};

export default CostExplanationPanel;
